import { IconAI } from "@/assets/icons/geist/IconAI";
import { IconPlusSquare } from "@/assets/icons/geist/IconPlusSquare";
import { AnthropicLogo } from "@/assets/logos/AnthropicLogo";
import { AWSLogo } from "@/assets/logos/AWSLogo";
import { GCPLogo } from "@/assets/logos/GCPLogo";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { LLMIntegrationAddModal } from "@/components/runners/details/LLMIntegrationAddModal";
import { LLMIntegrationModal } from "@/components/runners/details/LLMIntegrationModal";
import { LLMIntegrationRemoveModal } from "@/components/runners/details/LLMIntegrationRemoveModal";
import {
    RunnerDetailsSection,
    type RunnerDetailsSectionProps,
    type SectionStatus,
} from "@/components/runners/details/RunnerDetailsSection";
import { type LLMProviderKind, parseProviderFromEndpoint, Provider } from "@/components/runners/details/types";

import { useFlagOnaAIEnabled, useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { TrackLocation } from "@/hooks/use-segment";
import { useListRunnerLLMIntegrations } from "@/queries/runner-configuration-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { LLMIntegrationPhase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { LLMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { type Runner } from "gitpod-next-api/gitpod/v1/runner_pb";
import type React from "react";
import { type FC, useCallback, useState } from "react";
import { getModelDisplayName } from "@/components/runners/details/llm-integration-utils";

type ModalState =
    | { type: "closed" }
    | { type: "add-integration"; provider?: LLMProviderKind }
    | { type: "select-provider" }
    | {
          type: "edit-integration";
          integration: LLMIntegration;
      }
    | {
          type: "remove-integration";
          integration: LLMIntegration;
      };

export const LLMIntegrationSection: FC<{
    runner: PlainMessage<Runner>;
    hasStack: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, hasStack, "data-track-location": dataTrackLocation }) => {
    return (
        <LLMIntegrationProvider
            runner={runner}
            hasStack={hasStack}
            Container={RunnerDetailsSection}
            withDescription={true}
            data-track-location={dataTrackLocation}
        />
    );
};

export const LLMIntegrationProvider: FC<{
    runner: PlainMessage<Runner>;
    hasStack: boolean;
    Container: React.ComponentType<RunnerDetailsSectionProps>;
    withDescription: boolean;
    "data-track-location": (modalState: ModalState) => TrackLocation;
}> = ({ runner, hasStack, Container, withDescription, "data-track-location": dataTrackLocation }) => {
    const { value: isOnaAIEnabled } = useFlagOnaAIEnabled();
    const {
        data: llmData,
        isLoading: isLoadingIntegrations,
        error,
    } = useListRunnerLLMIntegrations(runner.runnerId);
    const { membership, isPending: isLoadingMembership } = useMembership();
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });
    const isLoading = isLoadingIntegrations || isLoadingMembership;
    
    const integrations = llmData?.integrations || [];
    const managedByOnaIntelligence = llmData?.llmManagedByOna || false;

    const [modalState, setModalState] = useState<ModalState>({ type: "closed" });

    const onCloseModal = (open: boolean) => {
        if (!open) {
            setModalState({ type: "closed" });
        }
    };

    const onEditIntegration = useCallback(
        (integration: LLMIntegration) => {
            setModalState({
                type: "edit-integration",
                integration,
            });
        },
        [setModalState],
    );

    const supportsLLMIntegrations =
        (runner.provider === RunnerProvider.AWS_EC2 || runner.provider === RunnerProvider.GCP) && isOnaAIEnabled;
    if (!supportsLLMIntegrations) {
        return null;
    }

    const hasConfiguredIntegrations = integrations && integrations.length > 0;
    let status: SectionStatus = "loading";
    if (!isLoading) {
        if (!hasStack) {
            status = "locked";
        } else {
            status = hasConfiguredIntegrations ? "complete" : "optional";
        }
    }

    const ready = status !== "locked";

    // Fallback to admin check when feature flag is disabled
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const canCreateLLM = isGroupManagementEnabled
        ? hasPermission(ResourceType.RUNNER, runner.runnerId, "runnerllmintegration:create")
        : isAdmin;
    const canUpdateLLM = isGroupManagementEnabled
        ? hasPermission(ResourceType.RUNNER, runner.runnerId, "runnerllmintegration:update")
        : isAdmin;

    const readOnly = !ready || !canUpdateLLM;

    return (
        <Container status={status} title={withDescription ? "Configure LLM integrations" : undefined}>
            <div className="flex flex-col gap-1" data-testid="llm-integrations-section">
                <SkeletonBlock ready={!isLoading} className="mt-1 h-[100px]">
                    <div className="flex flex-col gap-4">
                        {withDescription && (
                            <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
                                <Text className={cn(!ready && "text-content-secondary", "text-base")}>
                                    Configure LLM providers to enable AI-powered development features in your
                                    environments.{" "}
                                    {ready && (
                                        <ExternalLink
                                            className="font-bold"
                                            href="https://ona.com/docs/ona/getting-started"
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            Read the docs.
                                        </ExternalLink>
                                    )}
                                </Text>
                            </div>
                        )}
                        <LLMIntegrationConfigurations
                            ready={ready}
                            error={error}
                            readOnly={readOnly}
                            canAddIntegration={canCreateLLM}
                            integrations={integrations || []}
                            isOnaIntelligence={managedByOnaIntelligence}
                            onAddIntegration={() => setModalState({ type: "select-provider" })}
                            onEditIntegration={onEditIntegration}
                        />
                    </div>
                </SkeletonBlock>
            </div>
            {modalState.type !== "closed" && (
                <Dialog open onOpenChange={onCloseModal}>
                    <DialogContent className="max-w-[600px]" data-track-location={dataTrackLocation(modalState)}>
                        {modalState.type === "add-integration" && (
                            <LLMIntegrationAddModal
                                runnerId={runner.runnerId}
                                provider={modalState.type === "add-integration" ? modalState.provider : undefined}
                                onClose={() => setModalState({ type: "closed" })}
                                data-track-location={dataTrackLocation(modalState)}
                            />
                        )}
                        {modalState.type === "select-provider" && (
                            <LLMProviderSelectModal
                                runnerProvider={runner.provider}
                                onSelect={(provider) => setModalState({ type: "add-integration", provider })}
                            />
                        )}
                        {modalState.type === "edit-integration" && (
                            <LLMIntegrationModal
                                runnerId={runner.runnerId}
                                integration={modalState.integration}
                                onClose={() => setModalState({ type: "closed" })}
                                data-track-location={dataTrackLocation(modalState)}
                            />
                        )}
                        {modalState.type === "remove-integration" && (
                            <LLMIntegrationRemoveModal
                                runnerId={runner.runnerId}
                                integration={modalState.integration}
                                onClose={() => setModalState({ type: "closed" })}
                                data-track-location={dataTrackLocation(modalState)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </Container>
    );
};

const LLMProviderSelectModal: FC<{
    runnerProvider: RunnerProvider;
    onSelect: (p: LLMProviderKind) => void;
}> = ({ runnerProvider, onSelect }) => {
    const allOptions = [
        { name: "AWS Bedrock", provider: Provider.Bedrock, Logo: AWSLogo },
        { name: "Google Vertex AI", provider: Provider.Vertex, Logo: GCPLogo },
        { name: "Anthropic", provider: Provider.Anthropic, Logo: AnthropicLogo },
    ];

    // Filter out Bedrock for GCP runners
    const options =
        runnerProvider === RunnerProvider.GCP
            ? allOptions.filter((opt) => opt.provider !== Provider.Bedrock)
            : allOptions;

    return (
        <div className="flex flex-col gap-4">
            <DialogHeader>
                <DialogTitle>Configure LLM provider</DialogTitle>
                <Text className="text-base text-content-secondary">
                    Configure LLM providers to enable AI-powered development features in your environments.
                </Text>
            </DialogHeader>
            <div className="flex flex-col gap-2">
                {options.map((opt) => (
                    <div
                        key={opt.name}
                        className="flex w-full items-center justify-between rounded-xl border-0.5 border-border-base bg-surface-secondary px-3 py-2"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex size-8 items-center justify-center rounded-lg">
                                <opt.Logo className="h-5 w-5 text-content-primary" />
                            </div>
                            <Text className="text-base font-bold">{opt.name}</Text>
                        </div>
                        <Button
                            variant="secondary"
                            onClick={() => onSelect(opt.provider as LLMProviderKind)}
                            data-tracking-id="select-llm-provider-llm-integration"
                        >
                            Select
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const LLMIntegrationConfigurations: FC<{
    ready: boolean;
    error: Error | null;
    integrations: LLMIntegration[];
    readOnly: boolean;
    canAddIntegration: boolean;
    isOnaIntelligence: boolean;
    onAddIntegration: () => void;
    onEditIntegration: (integration: LLMIntegration) => void;
}> = ({ ready, error, integrations, readOnly, canAddIntegration, isOnaIntelligence, onAddIntegration, onEditIntegration }) => {
    if (isOnaIntelligence) {
        return <OnaIntelligenceManagedConfiguration />;
    }

    if (!error && integrations.length === 0) {
        return <EmptyLLMIntegrationConfiguration readOnly={readOnly} canAddIntegration={canAddIntegration} onAddIntegration={onAddIntegration} />;
    }

    return (
        <div className="flex w-full flex-col gap-2">
            {error && (
                <div className="flex flex-col items-center">
                    <Text className="text-lg font-bold text-content-negative">Failed to list LLM integrations</Text>
                    <Text className="text-base text-content-negative">
                        Please try to refresh the page and contact support if the issue persists.
                    </Text>
                    <ErrorMessage error={error} />
                </div>
            )}
            {integrations.map((integration) => (
                <LLMIntegrationConfiguration
                    key={integration.id}
                    integration={integration}
                    readOnly={readOnly}
                    onEditIntegration={onEditIntegration}
                />
            ))}
            {ready && canAddIntegration && (
                <Button
                    data-testid="add-llm-integration-button"
                    variant="ghost"
                    onClick={onAddIntegration}
                    size="lg"
                    LeadingIcon={IconPlusSquare}
                    disabled={readOnly}
                    className="group w-full rounded-xl border-[1px] border-dashed px-3 py-2 hover:bg-surface-02 group-disabled:text-content-secondary"
                    data-tracking-id="add-llm-provider-llm-integration"
                >
                    Add a new provider
                </Button>
            )}
        </div>
    );
};

const LLMIntegrationConfiguration: FC<{
    integration: LLMIntegration;
    readOnly: boolean;
    onEditIntegration: (integration: LLMIntegration) => void;
}> = ({ integration, readOnly, onEditIntegration }) => {
    const getPhaseText = (phase: LLMIntegrationPhase) => {
        switch (phase) {
            case LLMIntegrationPhase.LLM_INTEGRATION_PHASE_AVAILABLE:
                return "Available";
            case LLMIntegrationPhase.LLM_INTEGRATION_PHASE_UNAVAILABLE:
                return "Unavailable";
            case LLMIntegrationPhase.LLM_INTEGRATION_PHASE_DISABLED:
                return "Disabled";
            default:
                return "Unknown";
        }
    };

    const getProviderName = (endpoint: string) => {
        const provider = parseProviderFromEndpoint(endpoint);
        switch (provider) {
            case Provider.Bedrock:
                return "AWS Bedrock";
            case Provider.Vertex:
                return "Google Vertex AI";
            case Provider.Anthropic:
                return "Anthropic";
            case Provider.Unknown:
                return "Unknown Provider";
        }
    };

    const getProviderLogo = (endpoint: string) => {
        const provider = parseProviderFromEndpoint(endpoint);
        switch (provider) {
            case Provider.Bedrock:
                return AWSLogo;
            case Provider.Vertex:
                return GCPLogo;
            case Provider.Anthropic:
                return AnthropicLogo;
            case Provider.Unknown:
                return IconAI;
        }
    };

    return (
        <div className="flex justify-between gap-4 rounded-xl border-0.5 border-border-base bg-surface-primary px-3 py-2">
            <div className="flex items-center gap-4">
                <div className="flex size-8 items-center justify-center rounded-lg">
                    {(() => {
                        const Logo = getProviderLogo(integration.endpoint);
                        if (Logo === IconAI) {
                            return <IconAI size="lg" className="text-content-primary" />;
                        }
                        const LogoComponent = Logo as FC<{ className?: string }>;
                        return <LogoComponent className="h-5 w-5 text-content-primary" />;
                    })()}
                </div>
                <div className="flex flex-col">
                    <div className="flex gap-1">
                        <Text className="text-base font-bold">{getProviderName(integration.endpoint)}</Text>
                        <Text className="text-base text-content-secondary">{integration.endpoint}</Text>
                    </div>
                    <Text className="text-sm">
                        Status: {getPhaseText(integration.phase)} • {getModelDisplayName(integration.models)}
                    </Text>
                </div>
            </div>
            <Button
                variant="secondary"
                disabled={readOnly}
                data-testid={`edit-llm-integration-${integration.id}`}
                onClick={() => onEditIntegration(integration)}
                data-tracking-id="edit-llm-integration-llm-integration"
            >
                Edit
            </Button>
        </div>
    );
};

const OnaIntelligenceManagedConfiguration: FC = () => {
    return (
        <Banner
            variant="info"
            text="LLM access is provided by Ona Intelligence. Contact your account manager to modify LLM integrations."
            LeadingIcon={<IconAI size="base" />}
        />
    );
};

const EmptyLLMIntegrationConfiguration: FC<{
    readOnly: boolean;
    canAddIntegration: boolean;
    onAddIntegration: () => void;
}> = ({ readOnly, canAddIntegration, onAddIntegration }) => {
    if (!canAddIntegration) {
        return null;
    }

    return (
        <div className="flex w-full flex-col items-center justify-center gap-3 rounded-lg">
            <div className="flex w-full flex-col gap-2">
                <Button
                    data-testid="add-llm-integration-button"
                    variant="ghost"
                    onClick={onAddIntegration}
                    size="lg"
                    LeadingIcon={IconPlusSquare}
                    disabled={readOnly}
                    className="group w-full rounded-xl border-[1px] border-dashed px-3 py-2 hover:bg-surface-02"
                    data-tracking-id="add-llm-integration-llm-integration"
                >
                    <Text className="flex h-9 w-full items-center group-disabled:text-content-secondary">
                        Add a new provider
                    </Text>
                </Button>
            </div>
        </div>
    );
};
